import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Radio,
  Crosshair,
  FileText,
  Settings,
  Bot,
  FlaskConical,
  ShieldCheck,
  Building2,
  Handshake,
  Scale,
  BookOpen,
} from 'lucide-react';

/**
 * ──────────────────────────────────────────────────────────────────────────
 * Navigation registry — the SINGLE source of truth for the group → child-page
 * system. Both the sidebar (AuthShell: icon + hover flyout) and the in-page tab
 * strip (TabShell) are derived from this one list, and App.tsx nests each
 * multi-page group's routes under a shared <TabShell/>. Change a label/path/order
 * here and every surface follows — no per-view hardcoded tab bars.
 *
 * A "group" is one sidebar icon:
 *   - 1 visible page  → plain icon (tooltip), no tab strip.
 *   - >1 visible page → icon + hover flyout, and a tab strip inside TabShell.
 *
 * Role visibility is declarative (group- and page-level). The role-specific Org
 * Settings route (admin vs manager) is just two role-gated pages with the same
 * label, so the registry stays one list rather than three.
 * ──────────────────────────────────────────────────────────────────────────
 */

export type Role = 'admin' | 'manager' | 'trader';
/** Resolved TapeLab level from /api/trader/me; null = no access. */
export type LabAccess = 'viewer' | 'researcher' | null;

// 'trade' = always-available core trader nav; 'alpha' = the password-gated
// dev/alpha zone (analytics + experimental, tested live on prod); 'manage' =
// role-gated org/admin. Section changes drive the sidebar dividers.
export type NavSection = 'manage' | 'trade' | 'alpha';

/** A child page within a group. Groups with >1 visible page show a tab strip. */
export interface NavPage {
  id: string;
  label: string;
  /** Absolute route path. Also how TabShell + the sidebar derive active state. */
  path: string;
  /** Small corner marker (e.g. 'test'). */
  badge?: string;
  /** Roles that can see this page. Omit ⇒ inherit the group's roles. */
  roles?: Role[];
}

/** A nav group = one sidebar icon + its child page(s). */
export interface NavGroup {
  id: string;
  label: string;
  icon: LucideIcon;
  section: NavSection;
  /** Small corner marker on the icon (e.g. 'g10'). */
  badge?: string;
  /** Roles that can see this group. Omit ⇒ all roles. */
  roles?: Role[];
  /** Existing resolved bot-access contract. Managers/Admins always satisfy it;
   *  traders satisfy it only when their session carries `botAccess: true`. */
  requiresBotAccess?: boolean;
  /** RFQ desk grant. Managers/Admins always satisfy it; traders only when
   *  their session carries `rfqAccess: true` (traderRules.rfqEnabled). */
  requiresRfqAccess?: boolean;
  /** Penelope panel. Managers/Admins always satisfy it (they read and may kill);
   *  traders only when their session carries `penelopeAccess: true`
   *  (traderRules.penelopeEnabled). */
  requiresPenelopeAccess?: boolean;
  /** TapeLab grant. Managers/Admins always satisfy it; traders only when their
   *  session carries a non-null `labAccess` (traderRules.labAccess). */
  requiresLabAccess?: boolean;
  /** Optional group-level toolbar rendered on the right of the TabShell header. */
  toolbar?: 'org';
  /** Render this group in the sidebar footer region instead of the main column. */
  footer?: boolean;
  pages: NavPage[];
}

export const NAV_GROUPS: NavGroup[] = [
  // ── manage (role-gated) ──────────────────────────────────────────────
  {
    id: 'administration',
    label: 'Administration',
    icon: ShieldCheck,
    section: 'manage',
    roles: ['admin'],
    pages: [
      { id: 'management', label: 'Management', path: '/administration/management' },
      { id: 'g1-health', label: 'G1 Health', path: '/administration/g1-health' },
    ],
  },
  {
    id: 'organization',
    label: 'Organization',
    icon: Building2,
    section: 'manage',
    roles: ['admin', 'manager'],
    toolbar: 'org',
    // One canonical Org Settings route for both roles — the page itself switches
    // to admin mode (org switcher) by role, so there's no role-specific path.
    pages: [
      { id: 'org-settings', label: 'Org Settings', path: '/organization/org-settings' },
      { id: 'org-account', label: 'Org Account', path: '/organization/org-account' },
      { id: 'trader-results', label: 'Trader Results', path: '/organization/trader-results' },
      { id: 'exports', label: 'Exports', path: '/organization/exports' },
      { id: 'cash-ledger-preview', label: 'Cash Ledger', path: '/organization/cash-ledger-preview', badge: 'preview' },
    ],
  },

  // ── trade (everyone, single-page icons) ──────────────────────────────
  { id: 'dashboard', label: 'Value Dashboard', icon: BarChart3, section: 'trade', pages: [{ id: 'dashboard', label: 'Value Dashboard', path: '/dashboard' }] },
  { id: 'positions', label: 'Position Tracker', icon: Crosshair, section: 'trade', pages: [{ id: 'positions', label: 'Position Tracker', path: '/positions' }] },
  { id: 'live', label: 'Live Discovery', icon: Radio, section: 'trade', pages: [{ id: 'live', label: 'Live Discovery', path: '/live' }] },
  { id: 'trades', label: 'My Trades', icon: FileText, section: 'trade', pages: [{ id: 'trades', label: 'My Trades', path: '/trades' }] },

  // ── alpha (lab + bots + rfq, each behind its own grant) ──────────────
  {
    // TapeLab — the research lab's viz experiments, embedded from the /api/lab
    // proxy. A framed external app with its own session.
    id: 'lab',
    label: 'Lab',
    icon: FlaskConical,
    section: 'alpha',
    requiresLabAccess: true,
    pages: [{ id: 'lab', label: 'Lab', path: '/lab' }],
  },
  {
    id: 'bots',
    label: 'Bots',
    icon: Bot,
    section: 'alpha',
    badge: 'g10',
    requiresBotAccess: true,
    pages: [
      // Operator-only, matching GET /api/manager/org/automation-status, which
      // 403s a trader. The route guard is <BotOperatorRoute/> in App.tsx.
      {
        id: 'automation',
        label: 'Automation Overview',
        path: '/bots/automation',
        roles: ['admin', 'manager'],
      },
      {
        id: 'vanilla',
        label: 'Vanilla MM',
        path: '/bots/vanilla',
        roles: ['admin', 'manager'],
      },
      { id: 'g9', label: 'G9 — Auto Assist', path: '/bots/g9', badge: 'test' },
      {
        id: 'marty',
        label: 'Marty the Market Maker',
        path: '/bots/marty',
        roles: ['admin', 'manager'],
      },
    ],
  },

  // Penelope — the per-game Pinnacle maker bot's control panel and status
  // screen. Its own group, NOT a page of Bots: the Bots group is gated on
  // botAccess, and the trader who owns Penelope games holds penelopeEnabled
  // without it. The relay re-checks the grant and ownership on every
  // /api/penelope request; this gate only decides whether the shell mounts it.
  {
    id: 'penelope',
    label: 'Penelope',
    icon: Scale,
    section: 'alpha',
    badge: 'bot',
    requiresPenelopeAccess: true,
    pages: [{ id: 'penelope', label: 'Penelope', path: '/penelope' }],
  },

  // RFQ lane (services/rfq on the relay). Operators by role, traders by the
  // per-user rfqEnabled grant — the relay's /api/rfq re-checks the principal
  // AND the grant on every request; this gate only decides whether the shell
  // mounts the surface.
  {
    id: 'rfq',
    label: 'RFQ',
    icon: Handshake,
    section: 'alpha',
    badge: 'v1',
    requiresRfqAccess: true,
    pages: [
      { id: 'board', label: 'Board', path: '/rfq/board' },
      { id: 'book', label: 'Book', path: '/rfq/book' },
      { id: 'shader', label: 'Shader', path: '/rfq/shader' },
      { id: 'manual', label: 'Manual', path: '/rfq/manual' },
      { id: 'request', label: 'Request', path: '/rfq/request' },
    ],
  },

  // StreamRift Sniper has NO rail icon (Josh, 2026-09-10): traders enter it from
  // the target icon on a game row (/sniper/:gameKey). The bare /sniper admin pad
  // still routes by URL — see ROUTES_WITHOUT_NAV_ENTRY in App.navigationContract.

  // ── footer ───────────────────────────────────────────────────────────
  { id: 'guide', label: 'Trader Guide', icon: BookOpen, section: 'trade', footer: true, pages: [{ id: 'guide', label: 'Trader Guide', path: '/guide' }] },
  { id: 'settings', label: 'Settings', icon: Settings, section: 'trade', footer: true, pages: [{ id: 'settings', label: 'Settings', path: '/settings' }] },
];

/** Does `pathname` fall within `path` (exact or a sub-route)? */
export function pathMatches(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(path + '/');
}

/** One shared predicate for both bot navigation and direct-route access. The
 * relay remains authoritative for each bot API operation; this only decides
 * whether the authenticated shell may expose/mount the bot surface. */
export function canAccessBotSurface(role: Role, botAccess = false): boolean {
  return role === 'admin' || role === 'manager' || botAccess;
}

/** The relay's org-level Vanilla-backed control planes remain operator-only.
 * Keeping this separate from the broad bot-surface grant prevents a trader's
 * navigation flag from becoming an org-fleet authority escalation. */
export function canAccessBotOperatorSurface(role: Role): boolean {
  return role === 'admin' || role === 'manager';
}

/** RFQ desk surface: operators always, traders only with the resolved
 * per-user grant. The relay re-reads traderRules on every /api/rfq request. */
export function canAccessRfq(role: Role, rfqAccess = false): boolean {
  return role === 'admin' || role === 'manager' || rfqAccess;
}

/** Penelope panel: anyone not switched off (the relay's fail-OPEN lock,
 * resolved into `penelopeAccess` by /me — everyone has Penelope), plus
 * operators always (the relay gives them read + disarm/kill/remove on their
 * org's games even when switched off). Same shape as before; the default
 * `false` only covers a /me that predates the field. */
export function canAccessPenelope(role: Role, penelopeAccess = false): boolean {
  return penelopeAccess || role === 'admin' || role === 'manager';
}

/** TapeLab surface: operators always, traders only with a resolved level. The
 * relay re-resolves the level on every handoff, so this only decides whether
 * the shell exposes/mounts the /lab page. */
export function canAccessLab(role: Role, labAccess: LabAccess = null): boolean {
  return role === 'admin' || role === 'manager' || labAccess === 'viewer' || labAccess === 'researcher';
}

/** Single-game live trading surface (/live/:gameKey): operators always,
 * traders only with the resolved per-user grant (traderRules.liveGameEnabled,
 * fail-closed). Display gating only — the order route independently enforces
 * one-click/LT2 capability on every order. */
export function canAccessLiveGame(role: Role, liveGameAccess = false): boolean {
  return role === 'admin' || role === 'manager' || liveGameAccess;
}

/** StreamRift Sniper per-game screen (/sniper/:gameKey) + the dashboard
 * target icon: admins, or a trader with the resolved fail-closed grant.
 * Deliberately NO manager role pass — sniper arms real-money runners, so
 * access is admin or an explicit per-user grant, nothing in between (the
 * relay enforces the same rule on every /api/sniper request). */
export function canAccessSniper(role: Role, sniperAccess = false): boolean {
  return role === 'admin' || sniperAccess;
}

/** Role-filter the registry: drop groups/pages the role can't see, then drop
 *  any group left with no visible pages. Returns groups in registry order. */
export function navForRole(
  role: Role,
  botAccess = false,
  rfqAccess = false,
  labAccess: LabAccess = null,
  penelopeAccess = false,
): NavGroup[] {
  return NAV_GROUPS.filter((g) =>
    (!g.roles || g.roles.includes(role)) &&
    (!g.requiresBotAccess || canAccessBotSurface(role, botAccess)) &&
    (!g.requiresRfqAccess || canAccessRfq(role, rfqAccess)) &&
    (!g.requiresLabAccess || canAccessLab(role, labAccess)) &&
    (!g.requiresPenelopeAccess || canAccessPenelope(role, penelopeAccess)))
    .map((g) => ({ ...g, pages: g.pages.filter((p) => !p.roles || p.roles.includes(role)) }))
    .filter((g) => g.pages.length > 0);
}

/** The group that owns the current path — for TabShell to render the right
 *  tab strip/toolbar. Pass the already role-filtered groups. */
export function groupForPath(groups: NavGroup[], pathname: string): NavGroup | undefined {
  return groups.find((g) => g.pages.some((p) => pathMatches(pathname, p.path)));
}

// Back-compat alias for the layout barrel (no live importers).
export type AppView = string;
